import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Copy, Check, Gift, Share2, Users } from "lucide-react";
import { ReferralQR } from "@/components/ReferralQR";

function CopyRow({ label, value, testId }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* noop */ }
  };
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-sm text-foreground">{value}</p>
      </div>
      <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-full" onClick={copy} data-testid={`${testId}-copy`}>
        {done ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function Referral() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("ref.meta_title"));
  const [me, setMe] = useState(null);
  const [links, setLinks] = useState(null);

  useEffect(() => {
    if (user?.role !== "coach") return;
    api.get("/referral/me").then(({ data }) => setMe(data)).catch(() => setMe({}));
    api.get("/me/referral/share-links").then(({ data }) => setLinks(data)).catch(() => setLinks({}));
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const code = me?.code || links?.code || "—";
  const rules = me?.reward_rules || {};
  const share = async () => {
    const url = links?.web_url || me?.url;
    if (navigator.share && url) { try { await navigator.share({ title: "My Within", url }); } catch { /* cancelled */ } }
    else if (url) { try { await navigator.clipboard.writeText(url); toast.success(t("ref.copied")); } catch { /* noop */ } }
  };

  return (
    <div data-testid="coach-referral-page">
      <Seo title={t("ref.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="ref-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("ref.eyebrow")} title={t("ref.title")} subtitle={t("ref.subtitle")} />

      {me === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center" data-testid="ref-code-card">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary"><Gift className="h-6 w-6" /></div>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ref.your_code")}</p>
            <p className="mt-1 font-display text-3xl tracking-wider text-foreground" data-testid="ref-code">{code}</p>
            <Button className="mt-4 gap-1.5 rounded-full" onClick={share} data-testid="ref-share"><Share2 className="h-4 w-4" /> {t("ref.share")}</Button>
            {(links?.web_url || me?.url) && (
              <div className="mt-5 flex justify-center">
                <ReferralQR value={links?.web_url || me?.url} testId="coach-referral-qr" />
              </div>
            )}
          </div>

          {links && (
            <div className="space-y-2">
              <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ref.links")}</p>
              {links.web_url && <CopyRow label={t("ref.web_link")} value={links.web_url} testId="ref-web" />}
              {links.app_url && <CopyRow label={t("ref.app_link")} value={links.app_url} testId="ref-app" />}
            </div>
          )}

          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="flex items-center gap-1.5 font-display text-lg text-foreground"><Users className="h-4 w-4 text-primary" /> {t("ref.rewards_title")}</p>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {rules.coach_client_bonus_pct != null && (
                <li>• {t("ref.rule_coach_bonus", { pct: Math.round((rules.coach_client_bonus_pct || 0) * 100), months: rules.coach_client_bonus_max_months || 0 })}</li>
              )}
              {rules.c2c_referrer_credit_eur_cents != null && (
                <li>• {t("ref.rule_c2c", { eur: (rules.c2c_referrer_credit_eur_cents / 100).toFixed(0), max: rules.c2c_max_rewards_per_year || 0 })}</li>
              )}
              <li className="text-xs italic">{t("ref.rule_note")}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
